import { useUser } from '@clerk/clerk-react';
import { useQuery } from 'convex/react';
import { api } from '../../api';

interface Device {
  deviceId: string;
  deviceName: string;
  serial?: string;
  associatedUsers: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    email?: string;
    identity: string;
  }>;
}

export const useUserDevice = () => {
  const { user, isLoaded: isClerkLoaded } = useUser();
  const clerkId = user?.id;

  // Step 1: Get Convex user ID from Clerk ID
  const convexUserId = useQuery(
    api.users.get.byClerkId,
    clerkId ? { id: clerkId } : "skip"
  );

  // Step 2: Get user's devices from Convex user ID
  const devicesResult = useQuery(
    api.users.get.byConvexId,
    convexUserId ? { userId: convexUserId } : "skip"
  );

  // Get the first device (primary device)
  const device = devicesResult?.[0] || null;

  // Step 3: Fetch device details to get the hardware serial
  // Only fetch if we have a device ID and user is authenticated
  const deviceDetailsResult = useQuery(
    api.devices.get.details,
    device?.deviceId ? { device: device.deviceId } : "skip"
  );

  // Extract the hardware serial from device details
  const hardwareSerial = deviceDetailsResult?.device?.serial || undefined;
  
  // Extract deviceConfig for settings fetching
  const deviceConfig = deviceDetailsResult?.device?.deviceConfig || null;

  // Find current user's identity from associatedUsers
  const currentUserIdentity = device?.associatedUsers?.find(
    (u: { userId: string }) => u.userId === convexUserId
  )?.identity as string | undefined;

  // Determine loading state - we're loading if:
  // - Clerk isn't loaded yet
  // - We have a Clerk ID but haven't resolved Convex user yet
  // - We have Convex user but haven't fetched devices yet
  // - We have a device but haven't fetched details yet
  const isLoading = !isClerkLoaded || 
    (!!clerkId && convexUserId === undefined) ||
    (!!convexUserId && devicesResult === undefined) ||
    (!!device?.deviceId && deviceDetailsResult === undefined);

  return {
    device: device ? {
      _id: device.deviceId,
      jssid: device.deviceId, // For backwards compatibility
      name: device.deviceName,
      serial: hardwareSerial, // Hardware serial from device details
      ...device
    } : null,
    devices: devicesResult || [],
    jssid: device?.deviceId,
    serial: hardwareSerial, // Expose serial directly for convenience
    deviceConfig, // Expose deviceConfig for settings fetching
    isLoading,
    error: null,
    isAuthenticated: !!user,
    convexUserId,
    currentUserIdentity
  };
};
